import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { LayoutHeader } from "@/Header";
import { Button } from "@/Button";
import { ValidateInput } from "@/Input";
import { Checkbox } from "@/Checkbox";
import ToggleNoTextSwitch from "@/ToggleNonTextSwitch";
import { Dialog } from "@/Dialog";
import {
  SubscriptionFormData,
  ServiceType,
  StartDateType,
  EndDateType,
} from "@/business/subscription/types";
import { formatPrice, badgeOptions } from "@/business/subscription/utils";
import {
  useCreateOrUpdateBusinessMypageTicket,
  useBusinessMypageTicketDetail,
  useDeleteBusinessMypageTicket,
} from "~/services/business/myPage";
import { format, addMonths, startOfMonth, endOfMonth, parse } from "date-fns";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import MessageModal from "@/MessageModal";
import chevronDown from "$/chevron-down.svg";

export const Route = createFileRoute(
  "/business/layout/user-subscription-add-edit"
)({
  component: SubscriptionAddEdit,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      productTicketSeq: search.productTicketSeq
        ? Number(search.productTicketSeq)
        : undefined,
      serviceType: (search.serviceType as ServiceType) || "유치원",
    };
  },
});

function SubscriptionAddEdit() {
  const navigate = useNavigate();
  const { productTicketSeq, serviceType } = useSearch({
    from: "/business/layout/user-subscription-add-edit",
  });
  const isEdit = !!productTicketSeq;

  // API 호출
  const { data: ticketDetail, isLoading: isDetailLoading } =
    useBusinessMypageTicketDetail(productTicketSeq);
  const createOrUpdateTicketMutation = useCreateOrUpdateBusinessMypageTicket();
  const deleteTicketMutation = useDeleteBusinessMypageTicket();

  const [isSaving, setIsSaving] = useState(false);
  const [isStartDateModalOpen, setIsStartDateModalOpen] = useState(false);
  const [isEndDateModalOpen, setIsEndDateModalOpen] = useState(false);
  const [isEndDateExpanded, setIsEndDateExpanded] = useState(false);
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  // 저장 시 종료일이 이미 지난 경우 사용자 확인용 (즉시 만료 상태로 저장됨)
  const [isPastEndDateConfirmOpen, setIsPastEndDateConfirmOpen] = useState(false);

  // ServiceType을 cdProductType으로 변환
  const getCdProductType = (serviceType: ServiceType): string => {
    const mapping: Record<ServiceType, string> = {
      유치원: "PRODUCT_TYPE_KINDERGARTEN",
      호텔: "PRODUCT_TYPE_HOTEL",
      데이케어: "PRODUCT_TYPE_DAYCARE",
      미용실: "PRODUCT_TYPE_GROOMING",
    };
    return mapping[serviceType] || serviceType;
  };

  const form = useForm<SubscriptionFormData>({
    defaultValues: {
      planName: "",
      content: "",
      price: "",
      discountRate: "0",
      badges: [],
      activeYn: "Y",
      isCountBased: false,
      count: "1",
      validityPeriodType: "dateRange",
      startDateType: "immediate",
      startDate: undefined,
      endDateType: "custom",
      endDate: format(addMonths(new Date(), 6), "yyyy-MM-dd"),
      usableDays: "30",
      usageDays: "30",
      autoStartEnabled: false,
      autoStartDays: "30",
      minPurchaseQty: "1",
      minUseQty: "1",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = form;

  const planName = watch("planName");
  const content = watch("content");
  const price = watch("price");
  const discountRate = watch("discountRate");
  const isCountBased = watch("isCountBased");
  const count = watch("count");
  // 호텔 이용권은 박(=일수) 단위 사용으로 splitYn=Y가 강제되며 UI 라벨/검증이 다르게 동작
  const isHotel = serviceType === "호텔";
  // 데이케어 시간권은 1회=1시간 단위이며 최소 구매/이용 수량을 추가로 설정함
  const isDaycare = serviceType === "데이케어";
  const minPurchaseQty = watch("minPurchaseQty");
  const minUseQty = watch("minUseQty");
  const validityPeriodType = watch("validityPeriodType");
  const startDateType = watch("startDateType");
  const startDate = watch("startDate");
  const endDateType = watch("endDateType");
  const endDate = watch("endDate");
  const usableDays = watch("usableDays");
  const usageDays = watch("usageDays");
  const autoStartEnabled = watch("autoStartEnabled");
  const autoStartDays = watch("autoStartDays");

  // 현재 편집중인 이용권이 만료 상태인지 (편집 진입 시점 기준).
  // 유효기간 유형이 "시작일-종료일 지정" (durationDays == null) + dtExpired ≤ 오늘.
  const isExistingTicketExpired = useMemo(() => {
    if (!isEdit || !ticketDetail) return false;
    const dt = (ticketDetail as any).dtExpired;
    const dur = (ticketDetail as any).durationDays;
    if (dur != null) return false;
    if (!dt) return false;
    return new Date(dt).getTime() <= Date.now();
  }, [isEdit, ticketDetail]);

  // 저장 시 폼 상태 기준으로 즉시-만료 여부 계산.
  // (사용자가 "시작일-종료일 지정" + 커스텀 종료일을 과거로 설정한 경우)
  const willBeExpiredOnSave = (): boolean => {
    if (validityPeriodType !== "dateRange") return false;
    let target: string | undefined;
    switch (endDateType) {
      case "custom":
        target = endDate || undefined;
        break;
      case "thisMonthEnd":
        target = format(endOfMonth(new Date()), "yyyy-MM-dd");
        break;
      case "nextMonthEnd":
        target = format(endOfMonth(addMonths(new Date(), 1)), "yyyy-MM-dd");
        break;
      default:
        target = undefined;
    }
    if (!target) return false;
    return new Date(target).getTime() <= Date.now();
  };

  // 저장 클릭 시 만족하지 못한 조건 메시지 목록 반환 (없으면 빈 배열)
  const getValidationMessages = (): string[] => {
    const messages: string[] = [];

    if (!planName?.trim()) messages.push("이름을 입력해주세요.");
    if (!content?.trim()) messages.push("내용을 입력해주세요.");

    if (!price?.trim()) {
      messages.push("금액을 입력해주세요.");
    } else if (!/^\d+$/.test(price)) {
      messages.push("금액은 숫자만 입력 가능합니다.");
    } else if (Number.parseInt(price, 10) < 300) {
      messages.push("금액은 300원 이상이어야 합니다.");
    }

    if (!discountRate?.trim()) {
      messages.push("할인율을 입력해주세요.");
    } else if (!/^\d+(\.\d+)?$/.test(discountRate)) {
      messages.push("할인율은 숫자만 입력 가능합니다.");
    } else {
      const num = Number.parseFloat(discountRate);
      if (num < 0 || num > 100) {
        messages.push("할인율은 0~100 사이의 값이어야 합니다.");
      }
    }

    // 할인 적용 후 최종 금액이 300원 이상이어야 함
    // (단가가 300원 미만이면 위 price 메시지로 안내되므로 cross-check 중복 방지)
    const priceNum = Number.parseInt(price || "", 10);
    const rateNum = Number.parseFloat(discountRate || "0");
    if (
      Number.isFinite(priceNum) &&
      priceNum >= 300 &&
      Number.isFinite(rateNum) &&
      rateNum >= 0 &&
      rateNum <= 100
    ) {
      const finalAmount = Math.floor(priceNum * (1 - rateNum / 100));
      if (finalAmount < 300) {
        messages.push("할인 적용 후 금액은 300원 이상이어야 합니다.");
      }
    }

    if (isCountBased && !isDaycare) {
      if (!count?.trim()) {
        messages.push(isHotel ? "박수를 입력해주세요." : "횟수를 입력해주세요.");
      } else if (!/^\d+$/.test(count)) {
        messages.push(
          isHotel
            ? "박수는 숫자만 입력 가능합니다."
            : "횟수는 숫자만 입력 가능합니다.",
        );
      } else {
        const n = Number.parseInt(count, 10);
        if (isHotel ? n < 1 : n < 2) {
          messages.push(
            isHotel ? "박수는 1 이상이어야 합니다." : "횟수는 2 이상이어야 합니다.",
          );
        }
      }
    }

    if (isDaycare) {
      // 총 이용 시간(count = splitCount). 1회 = 1시간, 최소 1시간
      const hoursNum = Number.parseInt(count || "0", 10);
      if (!count?.trim()) {
        messages.push("총 이용 시간을 입력해주세요.");
      } else if (!/^\d+$/.test(count)) {
        messages.push("총 이용 시간은 숫자만 입력 가능합니다.");
      } else if (hoursNum < 1) {
        messages.push("총 이용 시간은 1 이상이어야 합니다.");
      }

      // 최소 이용 수량은 총 이용 시간을 초과할 수 없음 (초과 시 예약 자체가 불가)
      const minUseNum = Number.parseInt(minUseQty || "0", 10);
      if (
        /^\d+$/.test(count || "") &&
        /^\d+$/.test(minUseQty || "") &&
        minUseNum > hoursNum
      ) {
        messages.push("최소 이용 시간은 총 이용 시간보다 클 수 없습니다.");
      }

      if (!minPurchaseQty?.trim()) {
        messages.push("최소 구매 수량을 입력해주세요.");
      } else if (!/^\d+$/.test(minPurchaseQty)) {
        messages.push("최소 구매 수량은 숫자만 입력 가능합니다.");
      } else if (Number.parseInt(minPurchaseQty, 10) < 1) {
        messages.push("최소 구매 수량은 1 이상이어야 합니다.");
      }

      if (!minUseQty?.trim()) {
        messages.push("최소 이용 시간을 입력해주세요.");
      } else if (!/^\d+$/.test(minUseQty)) {
        messages.push("최소 이용 시간은 숫자만 입력 가능합니다.");
      } else if (Number.parseInt(minUseQty, 10) < 1) {
        messages.push("최소 이용 시간은 1 이상이어야 합니다.");
      }
    }

    if (validityPeriodType === "daysFromIssue") {
      if (!usableDays?.trim()) {
        messages.push("발급일로부터 사용 가능 일수를 입력해주세요.");
      } else if (!/^\d+$/.test(usableDays)) {
        messages.push("사용 가능 일수는 숫자만 입력 가능합니다.");
      } else if (Number.parseInt(usableDays, 10) < 30) {
        messages.push("사용 가능 일수는 30일 이상이어야 합니다.");
      }
    }

    if (validityPeriodType === "daysFromFirstUse") {
      if (!usageDays?.trim()) {
        messages.push("이용 시작일로부터 사용 가능 일수를 입력해주세요.");
      } else if (!/^\d+$/.test(usageDays)) {
        messages.push("사용 가능 일수는 숫자만 입력 가능합니다.");
      } else if (Number.parseInt(usageDays, 10) < 30) {
        messages.push("사용 가능 일수는 30일 이상이어야 합니다.");
      }

      if (autoStartEnabled) {
        if (!autoStartDays?.trim()) {
          messages.push("자동 이용 시작 일수를 입력해주세요.");
        } else if (!/^\d+$/.test(autoStartDays)) {
          messages.push("자동 이용 시작 일수는 숫자만 입력 가능합니다.");
        } else if (Number.parseInt(autoStartDays, 10) < 1) {
          messages.push("자동 이용 시작 일수는 1일 이상이어야 합니다.");
        }
      }
    }

    return messages;
  };

  // 할인 적용 금액 계산
  const discountedPrice = useMemo(() => {
    const originalPrice = Number.parseInt(price || "0", 10);
    const rate = Number.parseFloat(discountRate || "0");
    if (originalPrice > 0 && rate >= 0) {
      return Math.floor(originalPrice * (1 - rate / 100));
    }
    return originalPrice;
  }, [price, discountRate]);

  // 날짜를 한국어 형식으로 변환
  const formatDateToKorean = (dateString: string) => {
    try {
      const date = parse(dateString, "yyyy-MM-dd", new Date());
      return format(date, "yyyy년 MM월 dd일");
    } catch {
      return dateString;
    }
  };

  // 시작일 계산
  const calculatedStartDate = useMemo(() => {
    const today = new Date();
    switch (startDateType) {
      case "immediate":
        return format(today, "yyyy-MM-dd");
      case "custom":
        return startDate || "";
      case "nextMonth":
        return format(startOfMonth(addMonths(today, 1)), "yyyy-MM-dd");
      default:
        return "";
    }
  }, [startDateType, startDate]);

  // 종료일 계산
  const calculatedEndDate = useMemo(() => {
    const today = new Date();
    switch (endDateType) {
      case "custom":
        return endDate || format(addMonths(today, 6), "yyyy-MM-dd");
      case "thisMonthEnd":
        return format(endOfMonth(today), "yyyy-MM-dd");
      case "nextMonthEnd":
        return format(endOfMonth(addMonths(today, 1)), "yyyy-MM-dd");
      default:
        return format(addMonths(today, 6), "yyyy-MM-dd");
    }
  }, [endDateType, endDate]);

  // 종료일 기본값 설정 (6개월 뒤)
  useEffect(() => {
    if (endDateType === "custom" && !endDate) {
      setValue("endDate", format(addMonths(new Date(), 6), "yyyy-MM-dd"));
    }
  }, [endDateType, endDate, setValue]);

  // 호텔 이용권은 항상 박 단위 → isCountBased 강제 ON, count 최소 1박
  useEffect(() => {
    if (isHotel) {
      setValue("isCountBased", true);
      const current = form.getValues("count");
      if (!current || Number.parseInt(current, 10) < 1) {
        setValue("count", "1");
      }
    }
  }, [isHotel, setValue, form]);

  // 데이케어 시간권은 1회=1시간 → 항상 횟수(시간) 단위. isCountBased 강제 ON, count(=총 시간 수) 최소 1
  useEffect(() => {
    if (isDaycare) {
      setValue("isCountBased", true);
      const current = form.getValues("count");
      if (!current || Number.parseInt(current, 10) < 1) {
        setValue("count", "1");
      }
    }
  }, [isDaycare, setValue, form]);

  // 수정 모드일 때 상세 데이터 로드
  useEffect(() => {
    if (isEdit && ticketDetail) {
      const detail = ticketDetail as any;
      const useDaysFromFirstUse = detail.usageDurationDays != null;
      const useDaysFromIssue =
        !useDaysFromFirstUse &&
        (detail.validityPeriodType === "daysFromIssue" ||
          detail.durationDays != null);
      reset({
        planName: detail.name,
        content: detail.contents || "",
        price: detail.cost.toString(),
        discountRate: detail.discount.toString(),
        badges: detail.badge || [],
        activeYn: detail.activeYn === "Y" ? "Y" : "N",
        isCountBased: detail.splitYn === "Y",
        count: detail.splitCount?.toString() || "1",
        validityPeriodType: useDaysFromFirstUse
          ? "daysFromFirstUse"
          : useDaysFromIssue
            ? "daysFromIssue"
            : "dateRange",
        startDateType: "immediate",
        startDate: detail.dtUsable || undefined,
        endDateType: "custom",
        endDate: detail.dtExpired || undefined,
        usableDays: detail.durationDays?.toString() ?? "30",
        usageDays: detail.usageDurationDays?.toString() ?? "30",
        autoStartEnabled: detail.autoStartDays != null,
        autoStartDays: detail.autoStartDays?.toString() ?? "30",
        minPurchaseQty: detail.minPurchaseQty?.toString() ?? "1",
        minUseQty: detail.minUseQty?.toString() ?? "1",
      });
    }
  }, [isEdit, ticketDetail, reset]);

  // 뱃지 변경 핸들러
  const handleBadgeChange = (badgeValue: string, checked: boolean) => {
    const currentBadges = watch("badges") || [];
    if (checked) {
      setValue("badges", [...currentBadges, badgeValue]);
    } else {
      setValue(
        "badges",
        currentBadges.filter((b) => b !== badgeValue)
      );
    }
  };

  // 이용권 발급 화면으로 이동
  // const _handleIssueClick = () => { 빌드에러 임시 수정
  //   if (productTicketSeq) {
  //     navigate({
  //       to: "/business/layout/user-subscription-issue",
  //       search: {
  //         productTicketSeq: productTicketSeq,
  //         serviceType: serviceType,
  //       },
  //     });
  //   }
  // };

  // 이용권 저장 (추가/수정)
  const onSubmit = async (data: SubscriptionFormData) => {
    setIsSaving(true);
    try {
      const originalPrice = Number.parseInt(data.price, 10);
      const rate = Number.parseFloat(data.discountRate || "0");
      const calculatedDiscountedPrice = Math.floor(
        originalPrice * (1 - rate / 100)
      );

      // 유효기간: 시작일·종료일 vs 발급일로부터 N일 vs 최초 이용일로부터 N일
      const today = new Date();
      let dtUsable: string | undefined;
      let dtExpired: string | undefined;
      let usableDaysValue: number | undefined;
      let usageDaysValue: number | undefined;
      let autoStartDaysValue: number | null = null;

      if (data.validityPeriodType === "daysFromFirstUse") {
        usageDaysValue = data.usageDays
          ? Number.parseInt(data.usageDays, 10)
          : 30;
        autoStartDaysValue = data.autoStartEnabled
          ? Number.parseInt(data.autoStartDays || "30", 10)
          : null;
      } else if (data.validityPeriodType === "daysFromIssue") {
        usableDaysValue = data.usableDays
          ? Number.parseInt(data.usableDays, 10)
          : 30;
      } else {
        // 시작일 계산
        switch (data.startDateType) {
          case "immediate":
            dtUsable = format(today, "yyyy-MM-dd");
            break;
          case "custom":
            dtUsable = data.startDate || format(today, "yyyy-MM-dd");
            break;
          case "nextMonth":
            dtUsable = format(startOfMonth(addMonths(today, 1)), "yyyy-MM-dd");
            break;
          default:
            dtUsable = format(today, "yyyy-MM-dd");
        }
        // 종료일 계산
        switch (data.endDateType) {
          case "custom":
            dtExpired =
              data.endDate || format(addMonths(today, 6), "yyyy-MM-dd");
            break;
          case "thisMonthEnd":
            dtExpired = format(endOfMonth(today), "yyyy-MM-dd");
            break;
          case "nextMonthEnd":
            dtExpired = format(endOfMonth(addMonths(today, 1)), "yyyy-MM-dd");
            break;
          default:
            dtExpired = format(addMonths(today, 6), "yyyy-MM-dd");
        }
      }

      // API 요청 데이터 구성
      // - 시작일/종료일 지정: dtUsable, dtExpired 전송, durationDays/usageDurationDays: null
      // - 발급일로부터 N일: durationDays 전송, dtUsable, dtExpired: null
      // - 최초 이용일로부터 N일: usageDurationDays(+autoStartDays) 전송, 나머지 날짜 필드: null
      const ticketRequest: any = {
        ...(isEdit &&
          productTicketSeq && {
            productTicketSeq: productTicketSeq,
          }),
        cdTicketType: "SUBSCRIPTION",
        cdProductType: getCdProductType(serviceType),
        name: data.planName,
        contents: data.content,
        cost: originalPrice,
        discount: rate,
        amount: calculatedDiscountedPrice,
        splitYn: data.isCountBased ? "Y" : "N",
        splitCount:
          data.isCountBased && data.count ? Number.parseInt(data.count, 10) : 1,
        badge: data.badges || [],
        activeYn: data.activeYn || "Y",
        dataOrder:
          isEdit && ticketDetail?.dataOrder !== undefined
            ? ticketDetail.dataOrder
            : 0,
        dtUsable: data.validityPeriodType === "dateRange" ? dtUsable : null,
        dtExpired: data.validityPeriodType === "dateRange" ? dtExpired : null,
        durationDays:
          data.validityPeriodType === "daysFromIssue"
            ? (usableDaysValue ?? 30)
            : null,
        usageDurationDays:
          data.validityPeriodType === "daysFromFirstUse"
            ? (usageDaysValue ?? 30)
            : null,
        autoStartDays:
          data.validityPeriodType === "daysFromFirstUse"
            ? autoStartDaysValue
            : null,
        // 데이케어 시간권: 최소 구매/이용 수량 (그 외 상품은 null)
        minPurchaseQty: isDaycare
          ? Number.parseInt(data.minPurchaseQty || "1", 10)
          : null,
        minUseQty: isDaycare
          ? Number.parseInt(data.minUseQty || "1", 10)
          : null,
      };

      // API 호출
      await createOrUpdateTicketMutation.mutateAsync(ticketRequest);

      // 성공 시 목록 화면으로 이동
      navigate({ to: "/business/layout/user-subscription-management" });
    } catch (error) {
      console.error("저장 실패:", error);
      // TODO: 에러 메시지 표시
    } finally {
      setIsSaving(false);
    }
  };

  // 이용권 삭제
  const handleDeleteClick = async () => {
    if (!productTicketSeq) return;

    setIsSaving(true);
    try {
      await deleteTicketMutation.mutateAsync(productTicketSeq);
      setIsDeleteConfirmVisible(false);
      navigate({ to: "/business/layout/user-subscription-management" });
    } catch (error) {
      console.error("삭제 실패:", error);
      // TODO: 에러 메시지 표시
    } finally {
      setIsSaving(false);
    }
  };

  if (isEdit && isDetailLoading) {
    return (
      <>
        <LayoutHeader title="이용권 수정" hasBackBtn hasFootPrint={false} />
        <div className="flex-1 min-h-0 overflow-y-auto flex justify-center items-center bg-[#F5F5F5]">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader
        title={isEdit ? "이용권 수정" : "이용권 추가"}
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const messages = getValidationMessages();
            if (messages.length > 0) {
              setValidationMessages(messages);
              setIsValidationModalOpen(true);
              return;
            }
            // 저장 시 종료일이 과거 → 즉시 만료 상태로 저장됨을 사장님에게 안내.
            // "확인" 하면 그대로 저장, "취소" 하면 폼으로 복귀해 유효기간 재조정.
            if (willBeExpiredOnSave()) {
              setIsPastEndDateConfirmOpen(true);
              return;
            }
            handleSubmit(onSubmit)();
          }}
          className="flex flex-col gap-4 p-4"
        >
          {/* 편집 진입 시 이미 만료 상태인 이용권 안내 배너 */}
          {isExistingTicketExpired && (
            <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              ⚠️ 이 이용권은 현재{" "}
              <span className="font-semibold">만료 상태</span>입니다. 유효기간을
              갱신하지 않으면 저장 후에도 발급이 불가합니다.
            </div>
          )}
          <div className="flex flex-col gap-3">
            <div>
              <label
                htmlFor="planName"
                className="block mb-1 text-sm font-medium text-gray-700"
              >
                이름 *
              </label>
              <ValidateInput
                id="planName"
                {...register("planName", {
                  required: "이용권 이름을 입력해주세요.",
                })}
                placeholder="예: 체험 및 상담권"
                errorMessage={errors.planName?.message}
              />
            </div>

            <div>
              <label
                htmlFor="content"
                className="block mb-1 text-sm font-medium text-gray-700"
              >
                내용 *
              </label>
              <ValidateInput
                id="content"
                {...register("content", {
                  required: "내용을 입력해주세요.",
                })}
                placeholder="픽드롭 서비스를 포함하고 있습니다."
                errorMessage={errors.content?.message}
              />
            </div>

            <div>
              <label
                htmlFor="price"
                className="block mb-1 text-sm font-medium text-gray-700"
              >
                금액 (원) *
              </label>
              <ValidateInput
                id="price"
                {...register("price", {
                  required: "금액을 입력해주세요.",
                  pattern: {
                    value: /^\d+$/,
                    message: "숫자만 입력 가능합니다.",
                  },
                })}
                type="number"
                placeholder="예: 100000"
                errorMessage={errors.price?.message}
              />
            </div>

            <div>
              <label
                htmlFor="discountRate"
                className="block mb-1 text-sm font-medium text-gray-700"
              >
                할인율 (%) *
              </label>
              <ValidateInput
                id="discountRate"
                {...register("discountRate", {
                  required: "할인율을 입력해주세요.",
                  pattern: {
                    value: /^\d+(\.\d+)?$/,
                    message: "숫자만 입력 가능합니다.",
                  },
                  validate: (value) => {
                    const num = Number.parseFloat(value);
                    if (num < 0 || num > 100) {
                      return "할인율은 0~100 사이의 값이어야 합니다.";
                    }
                    return true;
                  },
                })}
                type="number"
                step="0.1"
                placeholder="예: 10"
                errorMessage={errors.discountRate?.message}
              />
            </div>

            {/* 할인 적용 금액 표시 (읽기 전용) */}
            {price && discountRate && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">
                    할인 적용 금액
                  </span>
                  <span className="text-lg font-semibold text-orange-500">
                    {formatPrice(discountedPrice)}원
                  </span>
                </div>
                {Number.parseFloat(discountRate || "0") > 0 && (
                  <div className="mt-1 text-xs text-gray-500">
                    원가 {formatPrice(Number.parseInt(price || "0", 10))}원에서{" "}
                    {formatPrice(
                      Number.parseInt(price || "0", 10) - discountedPrice
                    )}
                    원 할인
                  </div>
                )}
              </div>
            )}

            {/* 뱃지 체크박스 */}
            <div>
              <div className="mb-2 text-sm font-medium text-gray-700">뱃지</div>
              <div className="grid grid-cols-2 gap-2">
                {badgeOptions.map((badge) => {
                  const isChecked =
                    watch("badges")?.includes(badge.value) || false;
                  return (
                    <Checkbox
                      key={badge.value}
                      title={badge.label}
                      checked={isChecked}
                      onChange={(e) =>
                        handleBadgeChange(badge.value, e.target.checked)
                      }
                    />
                  );
                })}
              </div>
            </div>

            {/* 호텔: 일수(박) 입력 / 그 외: 횟수이용권 */}
            {isHotel ? (
              <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200 min-h-[60px]">
                <div className="flex-1">
                  <div className="flex gap-3 justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      이용 일수(박)
                    </span>
                    <div className="flex gap-2 items-center max-w-[160px]">
                      <ValidateInput
                        {...register("count", {
                          required: "박 수를 입력해주세요.",
                          pattern: {
                            value: /^\d+$/,
                            message: "숫자만 입력 가능합니다.",
                          },
                          validate: (value) => {
                            const num = Number.parseInt(value || "0", 10);
                            if (!value || num < 1) {
                              return "1 이상의 숫자를 입력해주세요.";
                            }
                            return true;
                          },
                        })}
                        type="number"
                        min={1}
                        placeholder="박 수 입력"
                        errorMessage={errors.count?.message}
                        className="h-9 w-[100px]"
                      />
                      <span className="flex-shrink-0 text-sm text-gray-600">
                        박
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : isDaycare ? (
              // 데이케어는 아래 "데이케어 시간권 설정" 박스에서 총 이용 시간을 함께 입력
              null
            ) : (
              <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200 min-h-[60px]">
                <div className="flex-1">
                  <div className="flex gap-3 justify-between items-center">
                    <Checkbox
                      title="횟수이용권"
                      checked={isCountBased}
                      onChange={(e) => {
                        setValue("isCountBased", e.target.checked);
                        if (e.target.checked) {
                          setValue("count", "2");
                        } else {
                          setValue("count", "1");
                        }
                      }}
                    />
                    <div className="flex-1 max-w-[120px]">
                      <ValidateInput
                        {...register("count", {
                          required: isCountBased ? "횟수를 입력해주세요." : false,
                          pattern: {
                            value: /^\d+$/,
                            message: "숫자만 입력 가능합니다.",
                          },
                          validate: (value) => {
                            if (
                              isCountBased &&
                              (!value || Number.parseInt(value, 10) < 2)
                            ) {
                              return "2 이상의 숫자를 입력해주세요.";
                            }
                            return true;
                          },
                        })}
                        type="number"
                        min={isCountBased ? 2 : undefined}
                        placeholder="횟수 입력"
                        errorMessage={errors.count?.message}
                        className="h-9"
                        disabled={!isCountBased}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 데이케어 시간권: 총 이용 시간 / 최소 구매 수량 / 최소 이용 수량 (1회 = 1시간) */}
            {isDaycare && (
              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <div className="mb-1 text-sm font-medium text-gray-700">
                  데이케어 시간권 설정
                </div>
                <div className="mb-3 text-xs text-gray-500">
                  1회 = 1시간 단위로 발급됩니다.
                </div>
                <div className="flex flex-col gap-3">
                  {/* 총 이용 시간 */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">총 이용 시간</span>
                    <div className="flex gap-2 items-center">
                      <ValidateInput
                        {...register("count", {
                          required: "이용 시간을 입력해주세요.",
                          pattern: {
                            value: /^\d+$/,
                            message: "숫자만 입력 가능합니다.",
                          },
                          validate: (value) => {
                            const num = Number.parseInt(value || "0", 10);
                            if (!value || num < 1) {
                              return "1 이상의 숫자를 입력해주세요.";
                            }
                            return true;
                          },
                        })}
                        type="number"
                        min={1}
                        placeholder="예: 10"
                        errorMessage={errors.count?.message}
                        className="h-9 w-[96px] text-right"
                      />
                      <span className="w-8 text-sm text-gray-600">시간</span>
                    </div>
                  </div>

                  {/* 최소 이용 시간 */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">최소 이용 시간</span>
                    <div className="flex gap-2 items-center">
                      <ValidateInput
                        {...register("minUseQty", {
                          required: isDaycare
                            ? "최소 이용 시간을 입력해주세요."
                            : false,
                          pattern: {
                            value: /^\d+$/,
                            message: "숫자만 입력 가능합니다.",
                          },
                          validate: (value) => {
                            if (!isDaycare) return true;
                            if (!value || Number.parseInt(value, 10) < 1) {
                              return "1 이상의 숫자를 입력해주세요.";
                            }
                            return true;
                          },
                        })}
                        type="number"
                        min={1}
                        placeholder="예: 2"
                        errorMessage={errors.minUseQty?.message}
                        className="h-9 w-[96px] text-right"
                      />
                      <span className="w-8 text-sm text-gray-600">시간</span>
                    </div>
                  </div>

                  {/* 최소 구매 수량 */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">최소 구매 수량</span>
                    <div className="flex gap-2 items-center">
                      <ValidateInput
                        {...register("minPurchaseQty", {
                          required: isDaycare
                            ? "최소 구매 수량을 입력해주세요."
                            : false,
                          pattern: {
                            value: /^\d+$/,
                            message: "숫자만 입력 가능합니다.",
                          },
                          validate: (value) => {
                            if (!isDaycare) return true;
                            if (!value || Number.parseInt(value, 10) < 1) {
                              return "1 이상의 숫자를 입력해주세요.";
                            }
                            return true;
                          },
                        })}
                        type="number"
                        min={1}
                        placeholder="예: 2"
                        errorMessage={errors.minPurchaseQty?.message}
                        className="h-9 w-[96px] text-right"
                      />
                      <span className="w-8 text-sm text-gray-600">개</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 유효기간 설정 방식 선택 */}
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <div className="mb-3 text-sm font-medium text-gray-700">
                유효기간 설정
              </div>
              <div className="flex flex-col gap-3">
                <label className="flex gap-2 items-center">
                  <input
                    type="radio"
                    name="validityPeriodType"
                    value="dateRange"
                    checked={validityPeriodType === "dateRange"}
                    onChange={() => setValue("validityPeriodType", "dateRange")}
                    className="w-4 h-4 text-orange-500"
                  />
                  <span className="text-sm text-gray-700">
                    시작일 지정, 종료일 지정
                  </span>
                </label>
                <label className="flex gap-2 items-center">
                  <input
                    type="radio"
                    name="validityPeriodType"
                    value="daysFromIssue"
                    checked={validityPeriodType === "daysFromIssue"}
                    onChange={() =>
                      setValue("validityPeriodType", "daysFromIssue")
                    }
                    className="w-4 h-4 text-orange-500"
                  />
                  <span className="text-sm text-gray-700">
                    발급일로 부터 00일까지 사용
                  </span>
                </label>
                <label className="flex gap-2 items-center">
                  <input
                    type="radio"
                    name="validityPeriodType"
                    value="daysFromFirstUse"
                    checked={validityPeriodType === "daysFromFirstUse"}
                    onChange={() =>
                      setValue("validityPeriodType", "daysFromFirstUse")
                    }
                    className="w-4 h-4 text-orange-500"
                  />
                  <span className="text-sm text-gray-700">
                    보호자가 최초 이용일로부터 00일
                  </span>
                </label>
              </div>
            </div>

            {/* 발급일로 부터 N일까지 사용 (validityPeriodType === "daysFromIssue") */}
            {validityPeriodType === "daysFromIssue" && (
              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <div className="mb-2 text-sm font-medium text-gray-700">
                  발급일로 부터 사용 가능 일수
                </div>
                <div className="flex gap-2 items-center">
                  <ValidateInput
                    {...register("usableDays", {
                      required:
                        validityPeriodType === "daysFromIssue"
                          ? "일수를 입력해주세요."
                          : false,
                      pattern: {
                        value: /^\d+$/,
                        message: "숫자만 입력 가능합니다.",
                      },
                      validate: (value) => {
                        if (validityPeriodType !== "daysFromIssue") return true;
                        const num = Number.parseInt(value || "0", 10);
                        if (num < 30) return "30일 이상 입력해주세요.";
                        return true;
                      },
                    })}
                    type="number"
                    min={30}
                    placeholder="예: 30"
                    errorMessage={errors.usableDays?.message}
                    className="max-w-[120px]"
                  />
                  <span className="text-sm text-gray-600">일까지 사용</span>
                </div>
              </div>
            )}

            {/* 보호자 최초 이용일로부터 N일 (validityPeriodType === "daysFromFirstUse") */}
            {validityPeriodType === "daysFromFirstUse" && (
              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <div className="mb-2 text-sm font-medium text-gray-700">
                  이용 시작일로부터 사용 가능 일수
                </div>
                <div className="flex gap-2 items-center">
                  <ValidateInput
                    {...register("usageDays", {
                      required:
                        validityPeriodType === "daysFromFirstUse"
                          ? "일수를 입력해주세요."
                          : false,
                      pattern: {
                        value: /^\d+$/,
                        message: "숫자만 입력 가능합니다.",
                      },
                      validate: (value) => {
                        if (validityPeriodType !== "daysFromFirstUse")
                          return true;
                        const num = Number.parseInt(value || "0", 10);
                        if (num < 30) return "30일 이상 입력해주세요.";
                        return true;
                      },
                    })}
                    type="number"
                    min={30}
                    placeholder="예: 30"
                    errorMessage={errors.usageDays?.message}
                    className="max-w-[120px]"
                  />
                  <span className="text-sm text-gray-600">
                    일까지 사용 가능
                  </span>
                </div>
                <div className="mt-2 p-2 text-xs text-orange-500 bg-orange-50 rounded">
                  보호자가 처음 이용한 날부터 유효기간이 계산됩니다.
                </div>

                {/* 자동 이용 시작 처리 (선택 옵션) */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-700">
                        자동 이용 시작 처리
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        발급일로부터 설정한 일수 초과 시 자동으로 이용
                        시작됩니다.
                      </div>
                    </div>
                    <ToggleNoTextSwitch
                      checked={autoStartEnabled}
                      onChange={(checked: boolean) => {
                        setValue("autoStartEnabled", checked);
                        if (checked && !autoStartDays?.trim()) {
                          setValue("autoStartDays", "30");
                        }
                      }}
                    />
                  </div>
                  {autoStartEnabled && (
                    <div className="flex gap-2 items-center mt-2">
                      <span className="text-sm text-gray-600">
                        발급일로부터
                      </span>
                      <ValidateInput
                        {...register("autoStartDays", {
                          required: autoStartEnabled
                            ? "일수를 입력해주세요."
                            : false,
                          pattern: {
                            value: /^\d+$/,
                            message: "숫자만 입력 가능합니다.",
                          },
                          validate: (value) => {
                            if (!autoStartEnabled) return true;
                            const num = Number.parseInt(value || "0", 10);
                            if (num < 1) return "1일 이상 입력해주세요.";
                            return true;
                          },
                        })}
                        type="number"
                        min={1}
                        placeholder="예: 30"
                        errorMessage={errors.autoStartDays?.message}
                        className="max-w-[100px]"
                      />
                      <span className="text-sm text-gray-600">
                        일 초과 시 자동 시작
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 시작일 지정 (dateRange일 때만) */}
            {validityPeriodType === "dateRange" &&
              (!isEdit ? (
                <div className="p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-700">
                      시작일 지정
                    </div>
                    {calculatedStartDate && (
                      <div className="text-sm font-medium text-orange-500">
                        {formatDateToKorean(calculatedStartDate)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-3">
                    <label className="flex gap-2 items-center">
                      <input
                        type="radio"
                        name="startDateType"
                        value="immediate"
                        checked={startDateType === "immediate"}
                        onChange={(e) => {
                          setValue(
                            "startDateType",
                            e.target.value as StartDateType
                          );
                        }}
                        className="w-4 h-4 text-orange-500"
                      />
                      <span className="text-sm text-gray-700">즉시 사용</span>
                    </label>

                    <label className="flex gap-2 items-center">
                      <input
                        type="radio"
                        name="startDateType"
                        value="custom"
                        checked={startDateType === "custom"}
                        onChange={(e) => {
                          setValue(
                            "startDateType",
                            e.target.value as StartDateType
                          );
                        }}
                        className="w-4 h-4 text-orange-500"
                      />
                      <span className="text-sm text-gray-700">
                        지정일부터 사용
                      </span>
                    </label>
                    {startDateType === "custom" && (
                      <div className="ml-6">
                        <Button
                          type="button"
                          onClick={() => setIsStartDateModalOpen(true)}
                          className="py-2 w-full text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300"
                        >
                          {startDate
                            ? formatDateToKorean(startDate)
                            : "날짜 선택"}
                        </Button>
                      </div>
                    )}

                    <label className="flex gap-2 items-center">
                      <input
                        type="radio"
                        name="startDateType"
                        value="nextMonth"
                        checked={startDateType === "nextMonth"}
                        onChange={(e) => {
                          setValue(
                            "startDateType",
                            e.target.value as StartDateType
                          );
                        }}
                        className="w-4 h-4 text-orange-500"
                      />
                      <span className="text-sm text-gray-700">
                        다음달부터 사용
                      </span>
                    </label>
                    {startDateType === "nextMonth" && (
                      <div className="ml-6">
                        <div className="p-2 text-xs text-orange-500 bg-orange-50 rounded">
                          보호자는 구매일을 기준으로 다음달부터 사용 가능합니다.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-2 items-center">
                      <div className="text-sm font-medium text-gray-700">
                        시작일
                      </div>
                      {ticketDetail && (ticketDetail as any).dtUsable && (
                        <div className="text-sm font-medium text-orange-500">
                          {formatDateToKorean((ticketDetail as any).dtUsable)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

            {/* 종료일 지정 (dateRange일 때만) */}
            {validityPeriodType === "dateRange" && (
              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <div
                  role={isEdit ? "button" : undefined}
                  tabIndex={isEdit ? 0 : undefined}
                  className={`flex justify-between items-center ${
                    isEdit ? "cursor-pointer hover:opacity-80" : ""}`}
                  onClick={() => {
                    if (isEdit) {
                      setIsEndDateExpanded(!isEndDateExpanded);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (isEdit && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      setIsEndDateExpanded(!isEndDateExpanded);
                    }
                  }}
                >
                  <div className="flex gap-2 items-center">
                    <div className="text-sm font-medium text-gray-700">
                      종료일 지정
                    </div>
                    {calculatedEndDate ? (
                      <div className="text-sm font-medium text-orange-500">
                        {formatDateToKorean(calculatedEndDate)}
                      </div>
                    ) : isEdit &&
                      ticketDetail &&
                      (ticketDetail as any).dtExpired ? (
                      <div className="text-sm font-medium text-orange-500">
                        {formatDateToKorean((ticketDetail as any).dtExpired)}
                      </div>
                    ) : null}
                  </div>
                  {isEdit && (
                    <img
                      src={chevronDown}
                      alt={isEndDateExpanded ? "접기" : "펼치기"}
                      className={`w-4 h-4 transition-transform duration-200 ${
                        isEndDateExpanded ? "rotate-180" : ""}`}
                    />
                  )}
                </div>
                {(!isEdit || isEndDateExpanded) && (
                  <div className="flex flex-col gap-3">
                    <label className="flex gap-2 items-center">
                      <input
                        type="radio"
                        name="endDateType"
                        value="custom"
                        checked={endDateType === "custom"}
                        onChange={(e) => {
                          const newType = e.target.value as EndDateType;
                          setValue("endDateType", newType);
                          // custom으로 변경 시 기존 endDate가 없으면 6개월 뒤로 설정
                          if (newType === "custom" && !endDate) {
                            setValue(
                              "endDate",
                              format(addMonths(new Date(), 6), "yyyy-MM-dd")
                            );
                          }
                        }}
                        className="w-4 h-4 text-orange-500"
                      />
                      <span className="text-sm text-gray-700">
                        지정일까지 사용
                      </span>
                    </label>
                    {endDateType === "custom" && (
                      <div className="ml-6">
                        <Button
                          type="button"
                          onClick={() => setIsEndDateModalOpen(true)}
                          className="py-2 w-full text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300"
                        >
                          {endDate ? formatDateToKorean(endDate) : "날짜 선택"}
                        </Button>
                      </div>
                    )}

                    <label className="flex gap-2 items-center">
                      <input
                        type="radio"
                        name="endDateType"
                        value="thisMonthEnd"
                        checked={endDateType === "thisMonthEnd"}
                        onChange={(e) => {
                          const newType = e.target.value as EndDateType;
                          setValue("endDateType", newType);
                          // thisMonthEnd로 변경 시 날짜 자동 계산
                          if (newType === "thisMonthEnd") {
                            setValue(
                              "endDate",
                              format(endOfMonth(new Date()), "yyyy-MM-dd")
                            );
                          }
                        }}
                        className="w-4 h-4 text-orange-500"
                      />
                      <span className="text-sm text-gray-700">
                        이번달 말일까지 사용
                      </span>
                    </label>
                    {endDateType === "thisMonthEnd" && (
                      <div className="ml-6">
                        <div className="p-2 text-xs text-orange-500 bg-orange-50 rounded">
                          보호자는 구매일을 기준으로 이번달까지 사용 가능합니다.
                        </div>
                      </div>
                    )}

                    <label className="flex gap-2 items-center">
                      <input
                        type="radio"
                        name="endDateType"
                        value="nextMonthEnd"
                        checked={endDateType === "nextMonthEnd"}
                        onChange={(e) => {
                          const newType = e.target.value as EndDateType;
                          setValue("endDateType", newType);
                          // nextMonthEnd로 변경 시 날짜 자동 계산
                          if (newType === "nextMonthEnd") {
                            setValue(
                              "endDate",
                              format(
                                endOfMonth(addMonths(new Date(), 1)),
                                "yyyy-MM-dd"
                              )
                            );
                          }
                        }}
                        className="w-4 h-4 text-orange-500"
                      />
                      <span className="text-sm text-gray-700">
                        다음달 말일까지 사용
                      </span>
                    </label>
                    {endDateType === "nextMonthEnd" && (
                      <div className="ml-6">
                        <div className="p-2 text-xs text-orange-500 bg-orange-50 rounded">
                          보호자는 구매일을 기준으로 다음달까지 사용 가능합니다.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 활성 여부 */}
            <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
              <div>
                <div className="text-sm font-medium text-gray-700">
                  활성 여부
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  비활성 시 이용권이 고객에게 표시되지 않습니다.
                </div>
              </div>
              <ToggleNoTextSwitch
                checked={watch("activeYn") === "Y"}
                onChange={(checked: boolean) => {
                  setValue("activeYn", checked ? "Y" : "N");
                }}
              />
            </div>
          </div>

          {/* 이용권 발급 버튼 (수정 모드일 때만 표시) */}
          {/* {!!(isEdit && productTicketSeq) && (
            <div className="mt-4">
              <Button
                type="button"
                onClick={handleIssueClick}
                className="py-3 w-full text-base font-semibold text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
              >
                이용권 발급
              </Button>
            </div>
          )} */}

          <div className="flex gap-3 pb-4 mt-4">
            <Button
              type="button"
              onClick={() =>
                navigate({
                  to: "/business/layout/user-subscription-management",
                })
              }
              className="flex-1 py-3 text-base font-semibold text-gray-600 bg-white rounded-lg border border-gray-300"
            >
              취소
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="flex-1 py-3 text-base font-semibold text-white bg-orange-400 rounded-lg disabled:opacity-50"
            >
              {isSaving ? "저장 중..." : "저장"}
            </Button>
          </div>
        </form>
      </div>

      {/* 입력값 확인 모달 (저장 클릭 시 미입력/오류 항목 안내) */}
      <Dialog
        visible={isValidationModalOpen}
        onClose={() => setIsValidationModalOpen(false)}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">입력 확인</div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-[#666666]">
              <div className="mb-2 text-center">
                아래 항목을 확인해 주세요.
              </div>
              <ul className="pl-4 list-disc">
                {validationMessages.map((msg, idx) => (
                  <li key={idx} className="mb-1">
                    {msg}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={() => setIsValidationModalOpen(false)}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 시작일 선택 모달 */}
      <MessageModal
        isOpen={isStartDateModalOpen}
        onRequestClose={() => setIsStartDateModalOpen(false)}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none animate-scaleIn"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          disablePast={true}
          dayOnClick={(day) => {
            if (day) {
              const dateString = format(day, "yyyy-MM-dd");
              setValue("startDate", dateString);
              setIsStartDateModalOpen(false);
            }
          }}
          selectedDay={
            startDate ? parse(startDate, "yyyy-MM-dd", new Date()) : null
          }
          schedules={{}}
        />
        <div className="flex gap-3 mt-2">
          <Button
            type="button"
            onClick={() => setIsStartDateModalOpen(false)}
            className="flex-1 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (startDate) {
                setIsStartDateModalOpen(false);
              }
            }}
            className="flex-1 py-2 text-sm font-semibold text-white bg-orange-400 rounded-full"
          >
            확인
          </Button>
        </div>
      </MessageModal>

      {/* 종료일 선택 모달 */}
      <MessageModal
        isOpen={isEndDateModalOpen}
        onRequestClose={() => setIsEndDateModalOpen(false)}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none animate-scaleIn"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          disablePast={true}
          dayOnClick={(day) => {
            if (day) {
              const dateString = format(day, "yyyy-MM-dd");
              setValue("endDate", dateString);
              setIsEndDateModalOpen(false);
            }
          }}
          selectedDay={
            endDate ? parse(endDate, "yyyy-MM-dd", new Date()) : null
          }
          schedules={{}}
        />
        <div className="flex gap-3 mt-2">
          <Button
            type="button"
            onClick={() => setIsEndDateModalOpen(false)}
            className="flex-1 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (endDate) {
                setIsEndDateModalOpen(false);
              }
            }}
            className="flex-1 py-2 text-sm font-semibold text-white bg-orange-400 rounded-full"
          >
            확인
          </Button>
        </div>
      </MessageModal>

      {/* 삭제 버튼 및 모달 (수정 모드일 때만) */}
      {!!(isEdit && productTicketSeq) && (
        <>
          <div className="flex justify-between px-6 pb-4">
            <button
              className="text-sm text-[#666666] hover:text-gray-700 transition-colors"
              onClick={() => setIsDeleteConfirmVisible(true)}
            >
              삭제
            </button>
          </div>

          {/* 삭제 확인 모달 */}
          <Dialog
            visible={isDeleteConfirmVisible}
            onClose={() => setIsDeleteConfirmVisible(false)}
            persist={false}
          >
            <div className="flex flex-col gap-1">
              <div className="flex flex-col mb-2">
                <div className="text-lg font-semibold text-center">
                  알림
                </div>
              </div>
              <div className="flex flex-col mb-2">
                <div className="text-sm text-center text-[#666666]">
                  정말로 이용권을 삭제하시겠습니까?
                </div>
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
                  onClick={() => setIsDeleteConfirmVisible(false)}
                >
                  취소
                </button>
                <button
                  className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                  onClick={handleDeleteClick}
                  disabled={isSaving}
                >
                  {isSaving ? "삭제 중..." : "삭제"}
                </button>
              </div>
            </div>
          </Dialog>
        </>
      )}

      {/* 종료일이 이미 지난 상태로 저장 시도 시 확인 */}
      <Dialog
        visible={isPastEndDateConfirmOpen}
        onClose={() => setIsPastEndDateConfirmOpen(false)}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">종료일 확인</div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              선택한 종료일이 이미 지났습니다.
              <br />
              저장 후 즉시{" "}
              <span className="font-semibold text-red-600">만료 상태</span>가
              되어
              <br />
              발급이 불가합니다. 그래도 저장할까요?
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
              onClick={() => setIsPastEndDateConfirmOpen(false)}
            >
              돌아가기
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={() => {
                setIsPastEndDateConfirmOpen(false);
                handleSubmit(onSubmit)();
              }}
              disabled={isSaving}
            >
              그래도 저장
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
